import {computed, Injectable, signal} from '@angular/core';
import {ActionCarouselButton, UpdateDragParams} from "../types/carousel.types";
import {minSwipeDistance} from "../configs/carousel-config";

@Injectable({
  providedIn: 'root'
})
export class CarouselSwipeService {
  private isDragging = signal(false);
  private startX = 0;
  private startY = 0;
  private dragOffset = signal<number>(0);

  public readonly dragging = computed(() => this.isDragging());

  public init(event: PointerEvent): void {
    this.isDragging.set(true);
    this.dragOffset.set(0);
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  public updateDrag(updateDragParams: UpdateDragParams): number | null {
    if (!this.isDragging()) { return null; }

    const { clientX, clientY, slideStep, visibleItems, containerWidth } = updateDragParams;
    const deltaX = clientX - this.startX;
    const deltaY = Math.abs(clientY - this.startY);

    if (deltaX !== 0 && deltaY < 30) {
      const itemWidth = containerWidth / visibleItems;
      const itemShift = deltaX / itemWidth;
      this.dragOffset.set(Math.max(-slideStep, Math.min(slideStep, itemShift)));
      return this.dragOffset();
    }

    return null;
  }

  public handleEnd(clientX: number): ActionCarouselButton | null {
    if (!this.isDragging()) { return null; }

    const deltaX = clientX - this.startX;
    this.isDragging.set(false);
    this.dragOffset.set(0);

    return Math.abs(deltaX) > minSwipeDistance
      ? (deltaX > 0 ? 'prev' : 'next')
      : null;
  }

  public cancel(): void {
    this.isDragging.set(false);
    this.dragOffset.set(0);
  }

  public handleWheel(event: WheelEvent): ActionCarouselButton | null {
    const isHorizontalScroll = event.deltaX !== 0;
    const isVerticalWithShift = event.deltaY !== 0 && event.shiftKey;
    if (!isHorizontalScroll && !isVerticalWithShift) { return null; }

    event.preventDefault();

    const normalizedDelta = isHorizontalScroll ? event.deltaX : event.deltaY;
    return normalizedDelta > 0 ? 'next' : 'prev';
  }
}
